import styled from "styled-components";
import BreadCrumbs from "../../../components/ui-v2/BreadCrumbs";
import { ROUTES } from "../../../router";
import Search from "../../../components/ui-v2/Inputs/Search";
import DropdownFilter from "../../../components/ui-v2/DropdownFilter";
import Button, { TWithIconVariants } from "../../../components/ui-v2/Button";
import {
  /* Table,  */ TCellStyleVariants,
  TTableBody,
} from "../../../components/ui-v2/TableGrid";
import { useNavigationManager } from "../../../hooks/useNavigationManager";
import { useTypedSelector } from "../../../hooks/useTypedSelector";
import {
  TMembership,
  TMembershipRole,
  TMembershipStatus,
} from "../../../redux/reducers/memberships";
import {
  backendErrorMessageHandler,
  decideMembershipRole,
  decideMembershipStatus,
  getQuerystringParams,
  handleGetDataFromLocalStorage,
} from "../../../utils/miscellaneous";
import Modal from "../../../components/ui-v2/Modal";
import { useEffect, useMemo, useRef, useState } from "react";

import { setUserMember, TUserData } from "../../../redux/reducers/userData";
import { useTypedDispatch } from "../../../hooks/useTypedDispatch";
import { setToastState } from "../../../redux/reducers/globalState";
import TableGrid, { TCellButton } from "../../../components/ui-v2/TableGrid";
import axios, { CancelTokenSource } from "axios";
import { deleteMember, fetchMemberships } from "../../../redux/actions";
import { useProportionalGridTemplateColumns } from "../../../hooks/useProportionalGridTemplateColumns";
import { localStorageConstants } from "../../../utils/constants";

const Container = styled.main`
  position: relative;

  & > h1 {
    font-family: var(--font-title-bold);
    font-size: 28px;
    color: var(--color-text-title);
    margin-top: 10px;
  }

  & > *:nth-child(1) {
    margin-top: 20px;
  }

  & > button {
    position: absolute;
    top: 0;
    right: 0;
  }

  tr {
    td:nth-child(8) {
      width: 10px;
    }
    td:nth-child(9) {
      button {
        border-color: var(--color-aux-danger-700);
        svg {
          color: var(--color-aux-danger-700);
        }
      }
    }
  }
`;

const ContentContainer = styled.div`
  margin-top: 20px;

  & > *:not(& > *:first-child) {
    margin-left: 16px;
  }

  display: flex;
  align-items: center;
`;

const Users: React.FC = () => {
  const { goTo, setQuerystring } = useNavigationManager();

  const dispatch = useTypedDispatch();

  const cancelTokenSource = useRef<CancelTokenSource | null>(null);

  const membershipData = useTypedSelector((state) => state.memberships.data);

  const isLoading = useTypedSelector(
    (state) => state.globalState.isLoadingMemberships
  );

  const userData = handleGetDataFromLocalStorage(
    localStorageConstants.USER_DATA
  ) as TUserData;

  const sidebarState = useTypedSelector((state) => state.sidebar);

  const reduxQuerystring = getQuerystringParams();

  const initialQuerystring = useRef(reduxQuerystring);

  // Ref to hold the working selection for the status filter
  const statusFilterRef = useRef<string[]>([]);
  // Ref to hold setSelected for use in onCancel
  const statusSetSelectedRef = useRef<React.Dispatch<
    React.SetStateAction<string[]>
  > | null>(null);

  // Ref to hold the working selection for the access profile filter
  const accessProfileFilterRef = useRef<string[]>([]);
  // Ref to hold setSelected for use in onCancel
  const accessProfileSetSelectedRef = useRef<React.Dispatch<
    React.SetStateAction<string[]>
  > | null>(null);

  const [memberToDelete, setMemberToDelete] = useState<TMembership | null>(
    null
  );

  const [shouldRejectFilledStatus, _setShouldRejectFilledStatus] =
    useState(false);
  const [shouldRejectFilledAccessProfile, _setShouldRejectFilledAccessProfile] =
    useState(false);

  const pageRef = useRef<number | undefined>();

  const [shouldOpenModal, setShouldOpenModal] = useState(false);

  const handleNameColumn = (member: TMembership) => {
    if (member.user && member.user?.uuid === userData?.uuid) {
      dispatch(setUserMember({ member }));

      return {
        values: [
          {
            style: "default",
            text:
              (member.user?.first_name ?? "") +
              " " +
              (member.user?.last_name ?? ""),
            shouldBreakLine: false,
          },
          {
            style: "faded",
            text: " (Minha conta)",
            shouldBreakLine: false,
          },
        ],
        style: "miscellaneous",
      };
    }

    if (!member.user?.first_name || member.user?.first_name === "") {
      return { value: "Sem nome", style: "faded" };
    }

    return {
      value:
        (member.user?.first_name ?? "") + " " + (member.user?.last_name ?? ""),
      style: "default",
    };
  };

  const handleEmailColumn = (member: TMembership) => ({
    value: member.email ?? member.user.email ?? "-",
    style: "default",
  });

  const handleAccessProfileColumn = (member: TMembership) => {
    switch (decideMembershipRole(member.role)) {
      case "Apenas leitura":
        return { value: "Apenas leitura", style: "darkGray", icon: "noIcon" };
      case "Colaborador":
        return { value: "Colaborador", style: "darkGray", icon: "noIcon" };
      case "Administrador":
        return { value: "Administrador", style: "blue", icon: "noIcon" };
    }
  };

  const handleOrganizationsColumn = (member: TMembership) => ({
    value: member.organizations_count,
    style: "bold",
  });

  const handleStatusColumn = (member: TMembership) => {
    switch (decideMembershipStatus(member.status)) {
      case "pending":
        return { value: "Pendente", style: "orange", icon: "noIcon" };
      case "active":
        return { value: "ativo", style: "green", icon: "noIcon" };
      case "disabled":
        return { value: "Inativo", style: "red", icon: "noIcon" };
    }
  };

  const handleActionsColumns = (member: TMembership, index: number) => {
    if (member.user && userData?.uuid === member.user?.uuid) {
      return [
        {
          icon: "pen",
          style: "button",
          "data-testid": "edit-button-" + index,
          onClick() {
            goTo(ROUTES.SETTINGS_USERS_EDIT + member.uuid, reduxQuerystring);
          },
          tooltip: {
            text: [
              { text: "Editar", shouldBreakLine: false, style: "default" },
            ],
          },
        } as TCellButton<TWithIconVariants>,
      ];
    }
    return [
      {
        icon: "pen",
        style: "button",
        "data-testid": "edit-button-" + index,
        onClick() {
          goTo(ROUTES.SETTINGS_USERS_EDIT + member.uuid, reduxQuerystring);
        },
        tooltip: {
          text: [{ text: "Editar", shouldBreakLine: false, style: "default" }],
        },
      } as TCellButton<TWithIconVariants>,
      {
        icon: "trash",
        style: "button",
        onClick() {
          if (memberToDelete === null || memberToDelete.uuid !== member.uuid) {
            setMemberToDelete(member);
            setShouldOpenModal(true);
          }
        },
        tooltip: {
          text: [{ text: "Excluir", shouldBreakLine: false, style: "default" }],
        },
      } as TCellButton<TWithIconVariants>,
    ];
  };

  function modalOnCancelHandler() {
    setShouldOpenModal(false);
  }

  function modalOnSuccessHandler() {
    setShouldOpenModal(false);

    if (memberToDelete) {
      dispatch(
        deleteMember({
          memberUuid: memberToDelete.uuid,
          cancelToken: (cancelTokenSource.current as CancelTokenSource).token,
        })
      )
        .unwrap()
        .then(() => {
          dispatch(
            setToastState({
              variant: "success",
              shouldShow: true,
              message: {
                title: "Sucesso",
                description:
                  (memberToDelete.email ??
                    memberToDelete.user.first_name +
                      memberToDelete.user.last_name) +
                  " teve o acesso removido",
              },
            })
          );
        })
        .catch((e) => {
          const errorMessage = backendErrorMessageHandler(e);
          console.log("error: ", e);
          dispatch(
            setToastState({
              variant: "error",
              shouldShow: true,
              message: {
                title: "Erro",
                description:
                  typeof errorMessage === "string"
                    ? errorMessage
                    : "Não foi possível remover o acesso de " +
                      (memberToDelete.email ??
                        memberToDelete.user.first_name +
                          memberToDelete.user.last_name),
              },
            })
          );
        });
    }
  }

  const fetchAndSetData = async () => {
    cancelTokenSource.current = axios.CancelToken.source();

    dispatch(
      fetchMemberships({ cancelToken: cancelTokenSource.current.token })
    );
  };

  const handleFilterStatus = (_selected: string, allSelect: string[]) => {
    const selectOptions = allSelect.map((select) => {
      switch (select) {
        case "1":
          return 1;
        case "2":
          return 2;
        case "3":
          return 0;
        case "0":
          return 3;
      }
    });

    let status: (TMembershipStatus | 3 | undefined)[] = [];

    if (selectOptions.length > 1) {
      if (selectOptions[1] === 0) {
        status = [selectOptions[1], selectOptions[0]];
      } else {
        status = selectOptions.sort((a, b) => {
          if (a && b) return a - b;
          return 0;
        });
      }
    } else if (selectOptions[0] !== undefined) {
      status.push(selectOptions[0]);
    }

    const querystring = { ...reduxQuerystring };
    querystring.status = status as number[];

    if (querystring.page) {
      querystring.page = 1;
    }

    setQuerystring(querystring);
  };

  const handleFilterRole = (_selected: string, allSelect: string[]) => {
    const selectOptions = allSelect.map((select) => {
      switch (select) {
        case "1":
          return 30;
        case "2":
          return 20;
        case "3":
          return 10;
        case "0":
          return 40;
      }
    });

    let role: (TMembershipRole | 40 | undefined)[] = [];

    if (selectOptions.length > 1) {
      role = selectOptions.sort((a, b) => {
        if (a && b) return a - b;
        return 0;
      });
    } else if (selectOptions[0] !== undefined) {
      role.push(selectOptions[0]);
    }

    const querystring = { ...reduxQuerystring };
    querystring.role = role as number[];

    if (querystring.page) {
      querystring.page = 1;
    }

    setQuerystring(querystring);
  };

  const handleOnClear = () => {
    const querystring = { ...reduxQuerystring };

    delete querystring.members_search;

    setQuerystring(querystring);

    dispatch(
      fetchMemberships({
        cancelToken: (cancelTokenSource.current as CancelTokenSource).token,
      })
    );
  };

  const handleOnSearch = (searchPhrase: string) => {
    const querystring = { ...reduxQuerystring };
    if (searchPhrase.length > 0) querystring.members_search = searchPhrase;
    else delete querystring.members_search;

    if (querystring.page) {
      querystring.page = 1;
    }

    setQuerystring(querystring);

    dispatch(
      fetchMemberships({
        cancelToken: (cancelTokenSource.current as CancelTokenSource).token,
      })
    );
  };

  // Robust customFilter for access profile

  const handleAccessProfileCustomFilter = (
    index: string,
    _selected: string[],
    setSelected: React.Dispatch<React.SetStateAction<string[]>>,
    selectDataRef: React.MutableRefObject<string[]>,
    prevSelectDataRef: React.MutableRefObject<string[]>,
    setShouldUpdate: React.Dispatch<React.SetStateAction<boolean>>
  ) => {
    // Store setSelected for use in onCancel
    accessProfileSetSelectedRef.current = setSelected;

    // Use accessProfileFilterRef as the source of truth
    let currentSelection = accessProfileFilterRef.current;
    prevSelectDataRef.current = [...currentSelection];

    let newSelection: string[] = [];
    if (index === "0") {
      // Selecting '0' makes it exclusive
      newSelection = ["0"];
    } else if (currentSelection.includes(index)) {
      // Deselect: remove only the clicked index
      newSelection = currentSelection.filter((v) => v !== index);
    } else {
      // Add index, but remove '0' if present
      newSelection = currentSelection.filter((v) => v !== "0");
      newSelection.push(index);
    }
    // Remove '0' if any other is selected
    if (newSelection.length > 1 && newSelection.includes("0")) {
      newSelection = newSelection.filter((v) => v !== "0");
    }
    // Ensure only unique values and no empty strings
    newSelection = Array.from(new Set(newSelection)).filter((v) => v !== "");
    accessProfileFilterRef.current = newSelection;

    selectDataRef.current = newSelection;
    setSelected(newSelection);
    setShouldUpdate(true);
    setTimeout(() => setShouldUpdate(false), 20);
  };

  const handleAccessProfileOnAction = (
    selectDataRef: React.MutableRefObject<string[]>
  ) => {
    handleFilterRole("", selectDataRef.current);
    // Update initialQuerystring to the new confirmed state
    initialQuerystring.current = {
      ...reduxQuerystring,
      role: selectDataRef.current.map((v) => {
        switch (v) {
          case "1":
            return 30;
          case "2":
            return 20;
          case "3":
            return 10;
          case "0":
            return 40;
          default:
            return 0;
        }
      }) as number[],
    };
    dispatch(
      fetchMemberships({
        cancelToken: (cancelTokenSource.current as CancelTokenSource).token,
      })
    );
  };

  // Robust onCancel for access profile (Home-style)
  const handleAccessProfileOnCancel = (
    selectDataRef: React.MutableRefObject<string[]>
  ) => {
    const reset = getDefaultAccessProfileOptions(reduxQuerystring) || [];
    accessProfileFilterRef.current = reset;
    selectDataRef.current = reset;
    setTimeout(() => {
      if (accessProfileSetSelectedRef.current) {
        accessProfileSetSelectedRef.current(reset);
      }
    }, 500);
  };

  const handleAccessProfileOnClear = () => {
    const querystring = { ...reduxQuerystring };

    delete querystring.role;

    if (querystring.page) {
      querystring.page = 1;
    }

    setQuerystring(querystring);

    dispatch(
      fetchMemberships({
        cancelToken: (cancelTokenSource.current as CancelTokenSource).token,
      })
    );
  };

  // Returns the default access profile options based on the querystring
  const getDefaultAccessProfileOptions = (queryStrings: any) => {
    if (queryStrings.role) {
      return queryStrings.role.map((role: number) => {
        switch (role as 10 | 20 | 30 | 40) {
          case 10:
            return "3";
          case 20:
            return "2";
          case 30:
            return "1";
          case 40:
            return "0";
        }
      });
    }
    return undefined;
  };

  // Keep ref in sync with querystring
  accessProfileFilterRef.current =
    getDefaultAccessProfileOptions(reduxQuerystring) || [];

  const handleAccessProfileForceSelectOptions = () => {
    return getDefaultAccessProfileOptions(reduxQuerystring) || [];
  };

  const handleStatusCustomFilter = (
    index: string,
    _selected: string[],
    setSelected: React.Dispatch<React.SetStateAction<string[]>>,
    selectDataRef: React.MutableRefObject<string[]>,
    prevSelectDataRef: React.MutableRefObject<string[]>,
    setShouldUpdate: React.Dispatch<React.SetStateAction<boolean>>
  ) => {
    // Store setSelected for use in onCancel
    statusSetSelectedRef.current = setSelected;

    let currentSelection = statusFilterRef.current;
    prevSelectDataRef.current = [...currentSelection];

    let newSelection: string[] = [];
    if (currentSelection.includes(index)) {
      // Deselect: remove only the clicked index
      newSelection = currentSelection.filter((v) => v !== index);
    } else if (index === "0") {
      // Selecting '0' makes it exclusive
      newSelection = ["0"];
    } else {
      // Add index, but remove '0' if present
      newSelection = currentSelection.filter((v) => v !== "0");
      newSelection.push(index);
    }
    // Ensure only unique values and no empty strings
    newSelection = Array.from(new Set(newSelection)).filter((v) => v !== "");
    statusFilterRef.current = newSelection;
    selectDataRef.current = newSelection;
    setSelected(newSelection);
    setShouldUpdate(true);
    setTimeout(() => setShouldUpdate(false), 20);
  };

  const handleStatusOnAction = (
    selectDataRef: React.MutableRefObject<string[]>
  ) => {
    handleFilterStatus("", selectDataRef.current);

    // Update initialQuerystring to the new confirmed state
    initialQuerystring.current = {
      ...reduxQuerystring,
      status: selectDataRef.current.map((v) => {
        switch (v) {
          case "1":
            return 1;
          case "2":
            return 2;
          case "3":
            return 0;
          case "0":
            return 3;
          default:
            return 0;
        }
      }) as number[],
    };
    dispatch(
      fetchMemberships({
        cancelToken: (cancelTokenSource.current as CancelTokenSource).token,
      })
    );
  };

  const handleStatusOnClear = () => {
    const querystring = { ...reduxQuerystring };

    delete querystring.status;

    if (querystring.page) {
      querystring.page = 1;
    }

    setQuerystring(querystring);

    dispatch(
      fetchMemberships({
        cancelToken: (cancelTokenSource.current as CancelTokenSource).token,
      })
    );
  };

  const handleStatusOnCancel = (
    selectDataRef: React.MutableRefObject<string[]>
  ) => {
    const reset = getDefaultStatusOptions(reduxQuerystring) || [];
    statusFilterRef.current = reset;
    selectDataRef.current = reset;
    setTimeout(() => {
      if (statusSetSelectedRef.current) {
        statusSetSelectedRef.current(reset);
      }
    }, 500);
  };

  // Returns the default status options based on the querystring
  const getDefaultStatusOptions = (queryStrings: any) => {
    if (queryStrings.status) {
      return queryStrings.status.map((status: number) => {
        switch (status as 0 | 1 | 2 | 3) {
          case 0:
            return "3";
          case 1:
            return "1";
          case 2:
            return "2";
          case 3:
            return "0";
        }
      });
    }
    return undefined;
  };

  const originalWidths = [0, 80, 650, 700, 325, 250, 250, 0, 0];

  const sidebarOffset = sidebarState.isFixed ? 240 : 48;

  const gridTemplateColumns =
    useProportionalGridTemplateColumns(originalWidths);

  // Keep ref in sync with querystring
  statusFilterRef.current = getDefaultStatusOptions(reduxQuerystring) || [];

  const handleStatusForceSelectOptions = () => {
    return getDefaultStatusOptions(reduxQuerystring) || [];
  };

  const tableBodyMemo = useMemo(
    () =>
      (membershipData?.results ?? []).map((member, index) => ({
        elementId: "",
        file_nfe: null,
        file_xml: null,
        body: [
          handleNameColumn(member),
          handleEmailColumn(member),
          handleAccessProfileColumn(member),
          handleOrganizationsColumn(member),
          handleStatusColumn(member),
          handleActionsColumns(member, index),
        ] as TTableBody<TCellStyleVariants>[],
      })),
    [membershipData]
  );

  useEffect(() => {
    fetchAndSetData();

    return () => {
      const querystring = getQuerystringParams();
      querystring.page = 1;
      delete querystring.members_search;
      delete querystring.visibility;
      delete querystring.status;
      delete querystring.role;
      setQuerystring(querystring);

      if (cancelTokenSource.current) cancelTokenSource.current.cancel();
    };
  }, []);

  return (
    <Container>
      <BreadCrumbs
        names={["Início", "Configurações", "Usuários"]}
        urls={[ROUTES.HOME, ROUTES.SETTINGS, ROUTES.SETTINGS_USERS]}
      />
      <h1>Gerenciar usuários</h1>
      <Button
        variant="primary"
        icon="plus"
        iconPosition="left"
        text="Novo usuário"
        onClick={() => {
          goTo(ROUTES.SETTINGS_USERS_CREATE, reduxQuerystring);
        }}
      />
      <ContentContainer>
        <Search
          key={reduxQuerystring.members_search + "-search"}
          onClear={handleOnClear}
          $ref={(ref, setSearchString) => {
            if (reduxQuerystring.members_search && ref.current) {
              const search = decodeURIComponent(
                reduxQuerystring.members_search
              ).replace(/\+/g, " ");
              console.log("search: ", search);
              ref.current.value = search;
              setSearchString(search);
            }
          }}
          variant="default"
          placeholder="Buscar usuários"
          onSearch={handleOnSearch}
        />
        <DropdownFilter
          menuVariant="unlabeled"
          buttonVariant="checkCircle"
          placeholder="Status"
          search={false}
          content={["Todos", "Ativo", "Inativo", "Pendente"]}
          customFilter={handleStatusCustomFilter}
          onAction={handleStatusOnAction}
          onCancel={handleStatusOnCancel}
          onClear={handleStatusOnClear}
          hasBottomBar
          key={reduxQuerystring.status + "-status"}
          forceSelectOptionsByIndex={(() => {
            const result = handleStatusForceSelectOptions();
            return shouldRejectFilledStatus
              ? undefined
              : result
              ? result.map(Number)
              : undefined;
          })()}
          filled={(() => {
            const result = handleStatusForceSelectOptions();
            return result === undefined
              ? result
              : [...result.toString().replaceAll(",", "")];
          })()}
        />
        <DropdownFilter
          menuVariant="unlabeled"
          buttonVariant="checkCircle"
          placeholder="Perfil de acesso"
          search={false}
          content={["Todos", "Administrador", "Colaborador", "Apenas leitura"]}
          customFilter={handleAccessProfileCustomFilter}
          onAction={handleAccessProfileOnAction}
          onClear={handleAccessProfileOnClear}
          onCancel={handleAccessProfileOnCancel}
          hasBottomBar
          key={reduxQuerystring.role + "-role"}
          forceSelectOptionsByIndex={(() => {
            const result = handleAccessProfileForceSelectOptions();
            return shouldRejectFilledAccessProfile
              ? undefined
              : result
              ? result.map(Number)
              : undefined;
          })()}
          filled={(() => {
            const result = handleAccessProfileForceSelectOptions();
            return result === undefined
              ? result
              : [...result.toString().replaceAll(",", "")];
          })()}
        />
      </ContentContainer>

      <TableGrid
        selectType={{ type: "noSelect" }}
        columns={{
          tableHead: {
            width: [gridTemplateColumns],
          },
          tableBody: {
            width: [gridTemplateColumns],
          },
        }}
        tableHead={{
          customPaddingTop: "0px",
          value: [
            { value: "Nome", style: "noIcon", showSkeleton: isLoading },
            { value: "E-mail", style: "noIcon", showSkeleton: isLoading },
            {
              value: "Perfil de acesso",
              style: "noIcon",
              showSkeleton: isLoading,
            },
            { value: "Organizações", style: "noIcon", showSkeleton: isLoading },
            { value: "Status", style: "noIcon", showSkeleton: isLoading },
            { value: "Ações", style: "noIcon", showSkeleton: isLoading },
          ],
        }}
        isLoading={isLoading}
        row="singleRow"
        emptyState={{
          isEmptyState: (membershipData?.results ?? []).length === 0,
          title: "Nenhum resultado encontrado",
          description: "Altere os filtros e realize a busca novamente",
        }}
        tableBody={
          isLoading
            ? new Array(5).fill("").map(() => ({
                elementId: "",
                file_nfe: null,
                file_xml: null,
                body: [
                  {
                    style: "default",
                    value: "",
                    skeleton: { values: [{ width: "177px" }] },
                  },
                  {
                    style: "default",
                    value: "",
                    skeleton: { values: [{ width: "177px" }] },
                  },
                  {
                    style: "default",
                    value: "",
                    skeleton: { values: [{ width: "120px" }] },
                  },
                  {
                    style: "default",
                    value: "",
                    skeleton: {
                      containerCss: { display: "flex", gap: "4px" },
                      values: [
                        {
                          width: "40px",
                        },
                      ],
                    },
                  },
                  {
                    style: "default",
                    value: "",
                    skeleton: {
                      containerCss: { display: "flex", gap: "4px" },
                      values: [
                        {
                          width: "80px",
                        },
                      ],
                    },
                  },
                  {
                    style: "default",
                    value: "",
                    skeleton: {
                      containerCss: {
                        display: "flex",
                        gap: "2px",
                        width: "80px",
                        overflow: "hidden",
                      },
                      values: [{ width: "32px" }, { width: "32px" }],
                    },
                  },
                ] as TTableBody<TCellStyleVariants>[],
              }))
            : tableBodyMemo
        }
        footer={{
          customStyle: {
            width: `calc(100vw - 48px - ${sidebarOffset}px)`,
          },
          customPagination: membershipData
            ? {
                fixedBottom: {
                  isFixedAtTheBottom: true,
                  customBottomPosition: "0px",
                },
                currentPage: `${membershipData?.current_page}`,
                buttonBackward: {
                  disabled: membershipData.current_page === 1,
                  onClick() {
                    if (membershipData.current_page === 1) {
                      pageRef.current = undefined;
                    } else {
                      pageRef.current = membershipData.current_page - 1;
                    }

                    const querystring = { ...reduxQuerystring };

                    if (membershipData.current_page !== 1) {
                      querystring.page = pageRef.current as number;
                      setQuerystring(querystring);

                      dispatch(
                        fetchMemberships({
                          cancelToken: (
                            cancelTokenSource.current as CancelTokenSource
                          ).token,
                        })
                      );
                    }
                  },
                },
                buttonForward: {
                  disabled:
                    membershipData.current_page === membershipData.pages.length,
                  onClick() {
                    if (
                      membershipData.current_page ===
                      membershipData.pages.length
                    ) {
                      pageRef.current = membershipData.current_page;
                    } else {
                      pageRef.current = membershipData.current_page + 1;
                    }

                    if (
                      membershipData.current_page !==
                      membershipData.pages.length
                    ) {
                      const querystring = { ...reduxQuerystring };
                      querystring.page = pageRef.current;
                      setQuerystring(querystring);

                      dispatch(
                        fetchMemberships({
                          cancelToken: (
                            cancelTokenSource.current as CancelTokenSource
                          ).token,
                        })
                      );
                    }
                  },
                },
                pages: membershipData.pages.map((page) => {
                  return {
                    value: Object.keys(page)[0],
                    onClick() {
                      let temp = (Object.values(page)[0] as string).match(
                        /.*?page=(\d+)/
                      );

                      if (temp !== null && temp[1]) {
                        pageRef.current = parseInt(temp[1]);
                      } else {
                        pageRef.current = undefined;
                      }

                      const querystring = { ...reduxQuerystring };
                      querystring.page = pageRef.current as number;
                      setQuerystring(querystring);
                      dispatch(
                        fetchMemberships({
                          cancelToken: (
                            cancelTokenSource.current as CancelTokenSource
                          ).token,
                        })
                      );
                    },
                  };
                }),
              }
            : undefined,
        }}
      />
      <Modal
        title="Excluir acesso"
        icon={{ type: "exclamationTriangle", color: "red" }}
        main={{
          style: "textOnly",
          content: [
            {
              text: "Tem certeza que deseja excluir o acesso de ",

              style: "default",
              shouldBreakLine: false,
            },
            {
              text:
                memberToDelete?.email ??
                memberToDelete?.user.first_name +
                  " " +
                  memberToDelete?.user.last_name,
              style: "bold",
              shouldBreakLine: false,
            },
            { text: "?", style: "default", shouldBreakLine: true },
          ],
        }}
        footer={{
          style: "default",
          buttons: [
            {
              variant: "secondary",
              icon: "noIcon",
              text: "Cancelar",
              onClick() {
                modalOnCancelHandler();
              },
            },
            {
              variant: "red",
              iconPosition: "left",
              icon: "checkmark",
              text: "Sim, excluir",
              onClick() {
                modalOnSuccessHandler();
              },
            },
          ],
        }}
        shouldOpen={shouldOpenModal}
        onClose={modalOnCancelHandler}
      />
    </Container>
  );
};

export default Users;
